import axios from 'axios';

/**
 * 백엔드 API와의 통신을 처리하는 Axios 기반 클라이언트
 * 
 * ✅ 백엔드 Phase 1~3 통합 대응:
 * - Phase 1: OpenAI Large 임베딩 (3072차원)
 * - Phase 2: 하이브리드 검색 (BM25 + Vector)
 * - Phase 3: Redis 캐싱 + Prometheus 메트릭
 * 
 * 모든 API 요청에 대한 기본 설정 및 에러 처리를 담당하며,
 * 인증, 로깅, 타임아웃 등의 공통 기능을 제공합니다.
 * 
 * 주요 기능:
 * - 통합 에러 핸들링: 401, 403, 404, 500 등 HTTP 상태 코드별 처리
 * - 요청/응답 인터셉터: 로깅 및 전처리/후처리
 * - 타임아웃 관리: 장시간 작업(인덱싱, 크롤링) 대응
 * - 캐싱 방지: 항상 최신 데이터 요청 (백엔드에서 Redis 캐싱 처리)
 */

// 기본 Axios 인스턴스 생성
const api = axios.create({
  // 환경 변수에서 API URL을 가져오거나 기본값 사용
  // 개발: http://localhost:8800/api
  // 프로덕션: /api (프록시 설정됨)
  baseURL: process.env.REACT_APP_API_URL || '/api',
  
  // 기본 헤더 설정
  headers: {
    'Content-Type': 'application/json',
    // 클라이언트 캐싱 방지 (백엔드 Redis 캐싱은 별도 처리)
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  
  // API 요청 타임아웃 설정 (기본 60초)
  // 크롤링/인덱싱 등 장시간 작업은 개별 API에서 override
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '60000'),
  
  // 요청 재시도 설정 (axios-retry 사용 시)
  // retry: 3,  // 3번까지 재시도
  // retryDelay: 1000,  // 1초 간격
});

// 요청 인터셉터 - 모든 요청이 전송되기 전에 처리
api.interceptors.request.use(
  (config) => {
    // 요청 정보 로깅
    console.log('API 요청:', config.method.toUpperCase(), config.baseURL + config.url, config.data);
    
    // 인증 토큰 추가 (현재는 비활성화 상태)
    // 로컬 스토리지에서 토큰 가져오기 (로그인 구현 시 활성화)
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    // 요청 전송 중 오류 발생 시 처리
    console.error('API 요청 인터셉터 오류:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 서버 응답을 처리하기 전에 가로채기
api.interceptors.response.use(
  (response) => {
    // 성공적인 응답 처리 및 로깅
    console.log('API 응답:', response.status, response.config.url);
    return response;
  },
  (error) => {
    if (error.response) {
      // 서버 응답이 있는 경우 (4xx, 5xx 에러)
      console.error('API 응답 에러:', {
        status: error.response.status,
        url: error.response.config.url,
        data: error.response.data,
        method: error.response.config.method,
        headers: error.response.headers
      });
      
      // 401 에러 (인증 실패) 처리
      if (error.response.status === 401) {
        // 인증 실패 시 로그아웃 및 로그인 페이지로 리디렉션
        // 로그아웃 처리 로직 (로그인 구현 시 활성화)
        // localStorage.removeItem('token');
        // window.location.href = '/login';
      }
    } else if (error.request) {
      // 요청이 발생했으나 응답이 없는 경우 (서버 다운, 네트워크 오류 등)
      console.error('서버 응답 없음:', {
        request: error.request,
        url: error.config?.url,
        method: error.config?.method
      });
    } else {
      // 요청 설정 중 에러가 발생한 경우 (잘못된 설정 등)
      console.error('요청 에러:', {
        message: error.message,
        config: error.config
      });
    }
    
    // 에러를 다시 throw하여 호출자가 처리할 수 있도록 함
    return Promise.reject(error);
  }
);

export default api; 